import React, { useState } from 'react';
import { X, Trash2, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import { Notebook, Language } from '../types';
import { restoreNotebookData, permanentlyDeleteNotebookData } from '../services/storageService';

interface TrashModalProps {
    isOpen: boolean;
    onClose: () => void;
    trashedNotebooks: Notebook[];
    language: Language;
    onRestored: (notebookId: string) => void;
    onPermanentlyDeleted: (notebookId: string) => void;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function daysUntilExpiry(deletedAt: number): number {
    const expiresAt = deletedAt + THIRTY_DAYS_MS;
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / (24 * 60 * 60 * 1000)));
}

function formatDeletedDate(deletedAt: number, language: Language): string {
    const date = new Date(deletedAt);
    return date.toLocaleDateString(language === 'vietnamese' ? 'vi-VN' : 'en-US', {
        day: 'numeric', month: 'short', year: 'numeric',
    });
}

export const TrashModal: React.FC<TrashModalProps> = ({
    isOpen, onClose, trashedNotebooks, language, onRestored, onPermanentlyDeleted,
}) => {
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    if (!isOpen) return null;

    const isVi = language === 'vietnamese';

    const handleRestore = async (notebookId: string) => {
        setLoadingId(notebookId);
        try {
            await restoreNotebookData(notebookId);
            onRestored(notebookId);
        } catch (e) {
            console.error('Restore failed', e);
        } finally {
            setLoadingId(null);
        }
    };

    const handlePermanentDelete = async (notebookId: string) => {
        setLoadingId(notebookId);
        setConfirmDeleteId(null);
        try {
            await permanentlyDeleteNotebookData(notebookId);
            onPermanentlyDeleted(notebookId);
        } catch (e) {
            console.error('Permanent delete failed', e);
        } finally {
            setLoadingId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
                style={{ maxHeight: '80vh' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-4 border-b dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-slate-950 shrink-0">
                    <div className="flex items-center gap-2">
                        <Trash2 size={18} className="text-red-500" />
                        <h3 className="font-bold text-base text-gray-900 dark:text-white">
                            {isVi ? 'Thùng rác' : 'Trash'}
                        </h3>
                        {trashedNotebooks.length > 0 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                ({trashedNotebooks.length})
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-slate-800 rounded-full transition-colors text-gray-500"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Info bar */}
                <div className="px-4 py-2.5 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-100 dark:border-amber-900/30 shrink-0">
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                        {isVi
                            ? 'Notebook trong thùng rác sẽ tự động bị xóa vĩnh viễn sau 30 ngày.'
                            : 'Notebooks in trash are permanently deleted after 30 days.'}
                    </p>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                    {trashedNotebooks.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-600">
                            <Trash2 size={40} className="mb-3 opacity-30" />
                            <p className="text-sm font-medium">{isVi ? 'Thùng rác trống' : 'Trash is empty'}</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-slate-800">
                            {trashedNotebooks.map(nb => {
                                const days = daysUntilExpiry(nb.deletedAt!);
                                const isLoading = loadingId === nb.id;
                                const isConfirming = confirmDeleteId === nb.id;

                                return (
                                    <li key={nb.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                                                {nb.title || (isVi ? '(Không có tiêu đề)' : '(Untitled)')}
                                            </p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                    {isVi ? 'Xóa lúc' : 'Deleted'} {formatDeletedDate(nb.deletedAt!, language)}
                                                </span>
                                                <span className={`text-[11px] font-medium ${days <= 3 ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                                    · {isVi ? `còn ${days} ngày` : `${days}d left`}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-1.5 shrink-0">
                                            {isConfirming ? (
                                                <>
                                                    <span className="text-xs text-red-600 dark:text-red-400 font-medium mr-1">
                                                        {isVi ? 'Chắc chắn?' : 'Sure?'}
                                                    </span>
                                                    <button
                                                        onClick={() => handlePermanentDelete(nb.id)}
                                                        disabled={isLoading}
                                                        className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                                                    >
                                                        {isLoading ? <Loader2 size={12} className="animate-spin" /> : (isVi ? 'Xóa' : 'Delete')}
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmDeleteId(null)}
                                                        className="px-2.5 py-1 text-xs font-medium rounded-lg bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300 transition-colors"
                                                    >
                                                        {isVi ? 'Hủy' : 'Cancel'}
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => handleRestore(nb.id)}
                                                        disabled={isLoading}
                                                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 transition-colors disabled:opacity-50"
                                                        title={isVi ? 'Khôi phục' : 'Restore'}
                                                    >
                                                        {isLoading ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                                                        {isVi ? 'Khôi phục' : 'Restore'}
                                                    </button>
                                                    <button
                                                        onClick={() => setConfirmDeleteId(nb.id)}
                                                        disabled={isLoading}
                                                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 border border-transparent hover:border-red-200 dark:hover:border-red-800 transition-colors disabled:opacity-50"
                                                        title={isVi ? 'Xóa vĩnh viễn' : 'Delete permanently'}
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};
